import React, { useState, useEffect, useCallback } from 'react';
import './career.css';
import Footer from '../Component/NewFooter';
import SplashScreen from '../Component/SplashScreen';
import axios from "axios";

const BASE_URL      = "https://api.jeyshawnng.com";
const FETCH_TIMEOUT = 8000;
// Bumped to v2: older cached payloads (a different shape than what
// parseCareersData currently produces) were making it past the "cached
// exists" check and crashing the render with pageData shaped wrong.
const CACHE_KEY      = "jeyshawn_careers_data_v2";

/* ── Helpers ───────────────────────────────────────────────────── */
function parseCareersData(db) {
  const paragraphs = Array.isArray(db?.paragraphs) ? db.paragraphs : [];
  const sections = [];
  let currentSection = null;

  paragraphs.forEach((p) => {
    if (p?.bold && p?.label) {
      currentSection = { heading: p.label, paragraphs: [] };
      sections.push(currentSection);
    } else if (p?.text && currentSection) {
      currentSection.paragraphs.push(p.text);
    }
  });

  // The "Why work with Us?" heading is stored as its own bold/label paragraph,
  // with its list items living separately in `bullets`.
  const whyItem = paragraphs.find((p) => p?.bold && p?.label?.toLowerCase().includes("why"));

  return {
    bannerImage:      db?.bannerImage      || "",
    title:            db?.title            || "",
    sections,
    whyWorkHeading:   whyItem?.label        || "",
    whyWorkItems:     Array.isArray(db?.bullets) ? db.bullets : [],
    applyButtonLabel: db?.applyButtonLabel || "Apply Now",
  };
}

// Guards against a stale/malformed localStorage payload (e.g. cached
// under an older shape) being treated as valid and crashing the render
// before the fetch can replace it with fresh, correctly-shaped data.
function isValidPageData(obj) {
  return (
    obj &&
    typeof obj === "object" &&
    typeof obj.bannerImage === "string" &&
    typeof obj.title === "string" &&
    Array.isArray(obj.sections) &&
    Array.isArray(obj.whyWorkItems)
  );
}

function readCache() {
  try {
    const parsed = JSON.parse(localStorage.getItem(CACHE_KEY));
    if (!isValidPageData(parsed)) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}

/* ── Sub-components ────────────────────────────────────────────── */
function ErrorScreen({ onRetry }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#09090b", zIndex: 9999,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px",
    }}>
      <div style={{ textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.1rem", fontFamily: "Georgia, serif", letterSpacing: "0.04em", marginBottom: "8px" }}>
          Poor internet connection.
        </p>
        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Please check your connection and try again.
        </p>
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: "8px", padding: "10px 32px", background: "transparent",
          border: "1px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.7)",
          fontSize: "0.8rem", letterSpacing: "0.12em", textTransform: "uppercase",
          cursor: "pointer", fontFamily: "Georgia, serif", transition: "border-color 0.2s, color 0.2s",
        }}
        onMouseEnter={e => { e.target.style.borderColor = "#d4af72"; e.target.style.color = "#d4af72"; }}
        onMouseLeave={e => { e.target.style.borderColor = "rgba(255,255,255,0.25)"; e.target.style.color = "rgba(255,255,255,0.7)"; }}
      >
        Try Again
      </button>
    </div>
  );
}

const formStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

  .appform__overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    overflow-y: auto;
    padding: 32px 16px 48px;
  }
  .appform__modal {
    background: #fff;
    width: 100%;
    max-width: 960px;
    border-radius: 10px;
    padding: 32px 28px 48px;
    font-family: 'Times New Roman', Times, serif;
    position: relative;
  }
  .appform__page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
  }
  .appform__page-title { font-size: 33px; font-weight: 600; color: #1a1a1a; letter-spacing: 0.01em; }
  .appform__close-btn {
    width: 28px; height: 28px; border-radius: 50%; border: 1.5px solid #ccc;
    background: transparent; cursor: pointer; display: flex; align-items: center;
    justify-content: center; color: #666; font-size: 16px; transition: border-color 0.2s, color 0.2s; flex-shrink: 0;
  }
  .appform__close-btn:hover { border-color: #3db87a; color: #3db87a; }
  .appform__section { margin-bottom: 36px; }
  .appform__section-header { display: flex; align-items: center; gap: 14px; margin-bottom: 6px; }
  .appform__section-title { font-size: 20px; font-weight: 600; color: #1a1a1a; white-space: nowrap; letter-spacing: 0.01em; }
  .appform__section-line { flex: 1; height: 1.5px; background: #3db87a; border-radius: 2px; }
  .appform__section-sublabel { font-size: 11px; color: #999; font-weight: 300; margin-bottom: 18px; font-style: italic; }
  .appform__row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px 28px; margin-bottom: 20px; }
  .appform__row--single { grid-template-columns: 1fr; }
  .appform__row--single .appform__group { max-width: 300px; }
  .appform__group { display: flex; flex-direction: column; gap: 5px; }
  .appform__group--full { grid-column: 1 / -1; max-width: 100% !important; }
  .appform__label { font-size: 16px; font-weight: 400; color: #666; letter-spacing: 0.01em; }
  .appform__label--optional { color: #999; }
  .appform__input {
    border: none; border-bottom: 1.5px solid #d4d4d4; background: transparent;
    font-family: 'Times New Roman', Times, serif; font-size: 18px; color: #1a1a1a;
    padding: 5px 0; outline: none; width: 100%; transition: border-color 0.2s;
  }
  .appform__input:focus { border-bottom-color: #3db87a; }
  .appform__textarea {
    border: none; border-bottom: 1.5px solid #d4d4d4; background: transparent;
    font-family: 'Times New Roman', Times, serif; font-size: 18px; color: #1a1a1a;
    padding: 5px 0; outline: none; width: 100%; resize: none; min-height: 52px; transition: border-color 0.2s;
  }
  .appform__textarea:focus { border-bottom-color: #3db87a; }
  .appform__select-wrap { position: relative; }
  .appform__select {
    appearance: none; -webkit-appearance: none; border: none; border-bottom: 1.5px solid #d4d4d4;
    background: transparent; font-family: 'Times New Roman', Times, serif; font-size: 18px; color: #1a1a1a;
    padding: 5px 24px 5px 0; outline: none; cursor: pointer; width: 100%; transition: border-color 0.2s;
  }
  .appform__select:focus { border-bottom-color: #3db87a; }
  .appform__select-arrow { position: absolute; right: 3px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #999; display: flex; }
  .appform__checkbox-label { font-size: 11.5px; font-weight: 400; color: #666; display: block; margin-bottom: 10px; }
  .appform__checkbox-group { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; }
  .appform__checkbox-item { display: flex; align-items: center; gap: 6px; cursor: pointer; }
  .appform__checkbox-item input[type="checkbox"] {
    appearance: none; -webkit-appearance: none; width: 13px; height: 13px; border: 1.5px solid #bbb;
    border-radius: 2px; cursor: pointer; position: relative; flex-shrink: 0; transition: border-color 0.2s, background 0.2s;
  }
  .appform__checkbox-item input[type="checkbox"]:checked { background: #3db87a; border-color: #3db87a; }
  .appform__checkbox-item input[type="checkbox"]:checked::after {
    content: ''; position: absolute; left: 2px; top: -1px; width: 5px; height: 8px;
    border: 2px solid #fff; border-top: none; border-left: none; transform: rotate(45deg);
  }
  .appform__checkbox-item span { font-size: 12px; color: #444; user-select: none; }
  .appform__upload-label { font-size: 13.5px; color: #666; margin-bottom: 8px; display: block; }
  .appform__upload-box {
    border: 1.5px dashed #d0d0d0; border-radius: 6px; padding: 28px 16px; text-align: center;
    margin-bottom: 18px; background: #fafafa; transition: border-color 0.2s, background 0.2s;
  }
  .appform__upload-box:hover { border-color: #3db87a; background: #f7fbf9; }
  .appform__upload-hint { font-size: 12px; color: #aaa; margin-bottom: 12px; }
  .appform__upload-success { font-size: 13px; color: #3db87a; font-weight: 500; margin-bottom: 10px; display: flex; align-items: center; justify-content: center; gap: 6px; }
  .appform__upload-btn {
    background: #3db87a; color: #fff; border: none; border-radius: 4px; padding: 7px 20px;
    font-family: 'Times New Roman', Times, serif; font-size: 14px; font-weight: 500; cursor: pointer;
    display: inline-flex; align-items: center; gap: 6px; transition: background 0.2s;
  }
  .appform__upload-btn:hover { background: #32a46a; }
  .appform__charcount { font-size: 10.5px; color: #3db87a; text-align: right; margin-top: 4px; }
  .appform__submit-wrap { display: flex; justify-content: flex-end; margin-top: 12px; }
  .appform__submit-btn {
    background: #3db87a; color: #fff; border: none; border-radius: 6px; padding: 11px 36px;
    font-family: 'Times New Roman', Times, serif; font-size: 13.5px; font-weight: 500; cursor: pointer; transition: background 0.2s;
  }
  .appform__submit-btn:hover { background: #32a46a; }
  .appform__submit-btn:disabled { background: #a0d9bc; cursor: not-allowed; }
  .appform__error { color: #e53935; font-size: 13px; text-align: center; margin-top: 12px; }
  .appform__success { color: #3db87a; font-size: 13px; text-align: center; margin-top: 12px; font-weight: 500; }
`;

/* ── Small reusable pieces ── */
const ChevronDown = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="4 6 8 10 12 6" />
  </svg>
);
const UploadIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const SectionHeader = ({ title }) => (
  <div className="appform__section-header">
    <span className="appform__section-title">{title}</span>
    <div className="appform__section-line" />
  </div>
);
const Field = ({ label, optional, children }) => (
  <div className="appform__group">
    <label className={`appform__label${optional ? " appform__label--optional" : ""}`}>{label}</label>
    {children}
  </div>
);
const Input = (props) => <input className="appform__input" {...props} />;
const SelectField = ({ label, options, value, onChange }) => (
  <Field label={label}>
    <div className="appform__select-wrap">
      <select className="appform__select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="" />
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
      <span className="appform__select-arrow"><ChevronDown /></span>
    </div>
  </Field>
);
const CheckboxGroup = ({ label, options, name, selected, onChange }) => (
  <div style={{ marginBottom: "8px" }}>
    <span className="appform__checkbox-label">{label}</span>
    <div className="appform__checkbox-group">
      {options.map(opt => (
        <label className="appform__checkbox-item" key={opt}>
          <input type="checkbox" name={name} value={opt} checked={selected === opt} onChange={() => onChange(opt)} />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  </div>
);
const UploadBox = ({ label, hint, mandatory, file, onUpload, inputId, accept }) => (
  <div>
    <span className="appform__upload-label">{label}{mandatory ? " – Mandatory" : ""}</span>
    <div className="appform__upload-box">
      {file ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "center" }}>
          <p className="appform__upload-success"><CheckIcon /> {file.name}</p>
          <button type="button" className="appform__upload-btn" onClick={() => document.getElementById(inputId)?.click()}>
            <UploadIcon /> Change File
          </button>
        </div>
      ) : (
        <>
          <p className="appform__upload-hint">{hint}</p>
          <button type="button" className="appform__upload-btn" onClick={() => document.getElementById(inputId)?.click()}>
            <UploadIcon /> Import
          </button>
        </>
      )}
    </div>
    <input type="file" id={inputId} accept={accept} style={{ display: "none" }} onChange={onUpload} />
  </div>
);

/* ── Application Form ── */
const ApplicationForm = ({ onClose }) => {
  const [fullName, setFullName] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [nationality, setNationality] = useState("");
  const [stateOfOrigin, setStateOfOrigin] = useState("");
  const [lga, setLga] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [residentialAddress, setResidentialAddress] = useState("");
  const [age, setAge] = useState("");
  const [stateOfResidence, setStateOfResidence] = useState("");
  const [residentialLocalGovernment, setResidentialLocalGovernment] = useState("");
  const [positionAppliedFor, setPositionAppliedFor] = useState("");
  const [department, setDepartment] = useState("");
  const [employmentType, setEmploymentType] = useState("");
  const [highestQualification, setHighestQualification] = useState("");
  const [institution, setInstitution] = useState("");
  const [courseOfStudy, setCourseOfStudy] = useState("");
  const [classGrade, setClassGrade] = useState("");
  const [yearOfGraduation, setYearOfGraduation] = useState("");
  const [otherQualifications, setOtherQualifications] = useState("");
  const [certificationName, setCertificationName] = useState("");
  const [issuingBody, setIssuingBody] = useState("");
  const [yearObtained, setYearObtained] = useState("");
  const [currentEmployer, setCurrentEmployer] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [keyResponsibilities, setKeyResponsibilities] = useState("");
  const [technicalSkills, setTechnicalSkills] = useState("");
  const [softSkills, setSoftSkills] = useState("");
  const [itProficiency, setItProficiency] = useState("");
  const [nyscStatus, setNyscStatus] = useState("");
  const [nyscCertificateNumber, setNyscCertificateNumber] = useState("");
  const [cvFile, setCvFile] = useState(null);
  const [coverLetterFile, setCoverLetterFile] = useState(null);
  const [certificatesFile1, setCertificatesFile1] = useState(null);
  const [certificatesFile2, setCertificatesFile2] = useState(null);
  const [certificatesFile3, setCertificatesFile3] = useState(null);
  const [certificatesFile4, setCertificatesFile4] = useState(null);
  const [certificatesFile5, setCertificatesFile5] = useState(null);
  const [whySuitable, setWhySuitable] = useState("");
  const [availability, setAvailability] = useState("");
  const maxChars = 500;
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleFileUpload = (e, setFile) => {
    const file = e.target.files[0];
    if (file) setFile(file);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const formData = new FormData();
      formData.append("fullName", fullName);
      formData.append("dateOfBirth", dob);
      formData.append("age", age);
      formData.append("gender", gender);
      formData.append("maritalStatus", maritalStatus);
      formData.append("nationality", nationality);
      formData.append("stateOfOrigin", stateOfOrigin);
      formData.append("lga", lga);
      formData.append("phoneNumber", phoneNumber);
      formData.append("email", email);
      formData.append("residentialAddress", residentialAddress);
      formData.append("stateOfResidence", stateOfResidence);
      formData.append("residentialLocalGovernment", residentialLocalGovernment);
      formData.append("positionAppliedFor", positionAppliedFor);
      formData.append("department", department);
      formData.append("employmentType", employmentType);
      formData.append("highestQualification", highestQualification);
      formData.append("institution", institution);
      formData.append("courseOfStudy", courseOfStudy);
      formData.append("classGrade", classGrade);
      formData.append("yearOfGraduation", yearOfGraduation);
      formData.append("otherQualifications", otherQualifications);
      formData.append("certificationName", certificationName);
      formData.append("issuingBody", issuingBody);
      formData.append("yearObtained", yearObtained);
      formData.append("currentEmployer", currentEmployer);
      formData.append("jobTitle", jobTitle);
      formData.append("keyResponsibilities", keyResponsibilities);
      formData.append("technicalSkills", technicalSkills);
      formData.append("softSkills", softSkills);
      formData.append("itProficiency", itProficiency);
      formData.append("nyscStatus", nyscStatus);
      formData.append("nyscCertificateNumber", nyscCertificateNumber);
      formData.append("whySuitable", whySuitable);
      formData.append("availability", availability);
      if (cvFile) formData.append("cv", cvFile);
      if (coverLetterFile) formData.append("coverLetter", coverLetterFile);
      if (certificatesFile1) formData.append("certificates1", certificatesFile1);
      if (certificatesFile2) formData.append("certificates2", certificatesFile2);
      if (certificatesFile3) formData.append("certificates3", certificatesFile3);
      if (certificatesFile4) formData.append("certificates4", certificatesFile4);
      if (certificatesFile5) formData.append("certificates5", certificatesFile5);

      const dataSubmit = await axios.post(`${BASE_URL}/api/applications`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSubmitSuccess(true);
      setTimeout(() => onClose(), 5000);
      console.log('sent data', dataSubmit);
    } catch (err) {
      console.error("Failed to submit application:", err);
      setSubmitError("Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{formStyles}</style>
      <div className="appform__overlay" onClick={handleOverlayClick}>
        <div className="appform__modal">
          <div className="appform__page-header">
            <span className="appform__page-title">Application Form</span>
            <button type="button" className="appform__close-btn" onClick={onClose}>✕</button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* 1 ── PERSONAL DETAILS */}
            <div className="appform__section">
              <SectionHeader title="Personal Details" />
              <p className="appform__section-sublabel">User personal data collected is solely for recruitment purposes.</p>
              <div className="appform__row appform__row--single">
                <Field label="Full Name"><Input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required /></Field>
              </div>
              <div className="appform__row">
                <Field label="Date of Birth"><Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} /></Field>
                <Field label="Age"><Input type="number" value={age} onChange={(e) => setAge(e.target.value)} /></Field>
              </div>
              <div className="appform__row">
                <Field label="Gender"><Input type="text" value={gender} onChange={(e) => setGender(e.target.value)} /></Field>
                <Field label="Nationality"><Input type="text" value={nationality} onChange={(e) => setNationality(e.target.value)} /></Field>
              </div>
              <div className="appform__row">
                <Field label="Marital Status (Optional)" optional><Input type="text" value={maritalStatus} onChange={(e) => setMaritalStatus(e.target.value)} /></Field>
                <Field label="State of Origin"><Input type="text" value={stateOfOrigin} onChange={(e) => setStateOfOrigin(e.target.value)} /></Field>
              </div>
              <div className="appform__row">
                <Field label="Email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
                <Field label="Local Government Area (LGA)"><Input type="text" value={lga} onChange={(e) => setLga(e.target.value)} /></Field>
              </div>
              <div className="appform__row">
                <Field label="Phone Number"><Input type="text" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required /></Field>
                <Field label="State of Residence"><Input type="text" value={stateOfResidence} onChange={(e) => setStateOfResidence(e.target.value)} /></Field>
              </div>
              <div className="appform__row">
                <Field label="Residential Address"><Input type="text" value={residentialAddress} onChange={(e) => setResidentialAddress(e.target.value)} /></Field>
                <Field label="Local Government of Residence"><Input type="text" value={residentialLocalGovernment} onChange={(e) => setResidentialLocalGovernment(e.target.value)} /></Field>
              </div>
            </div>

            {/* 2 ── POSITION INFORMATION */}
            <div className="appform__section">
              <SectionHeader title="Position Information" />
              <div className="appform__row">
                <Field label="Role Applied For"><Input type="text" value={positionAppliedFor} onChange={(e) => setPositionAppliedFor(e.target.value)} /></Field>
                <SelectField label="Department" value={department} onChange={setDepartment}
                  options={["Information Communication Technology (ICT)", "Catering", "Facility", "Audit & Compliance", "Human Resources", "Administrative", "Purchase Order & Service Entry Management", "Services", "Accounts", "Contract & Procurement", "Bid & Tender", "Business Development", "Document Control", "Logistics", "Engineering", "Assets", "Security", "QHSE", "Operations", "Corporate Services", "Project", "Others"]} />
              </div>
              <CheckboxGroup label="Employment Type" name="employment_type"
                options={["Full time", "Contract", "Internship / NYSC", "Ad-hoc"]}
                selected={employmentType} onChange={setEmploymentType} />
            </div>

            {/* 3 ── EDUCATIONAL QUALIFICATIONS */}
            <div className="appform__section">
              <SectionHeader title="Educational Qualifications" />
              <div className="appform__row">
                <Field label="Highest Qualification"><Input type="text" value={highestQualification} onChange={(e) => setHighestQualification(e.target.value)} /></Field>
                <Field label="Institution Attended"><Input type="text" value={institution} onChange={(e) => setInstitution(e.target.value)} /></Field>
              </div>
              <div className="appform__row">
                <Field label="Course of Study"><Input type="text" value={courseOfStudy} onChange={(e) => setCourseOfStudy(e.target.value)} /></Field>
                <Field label="Class / Grade (if applicable)"><Input type="text" value={classGrade} onChange={(e) => setClassGrade(e.target.value)} /></Field>
              </div>
              <div className="appform__row">
                <Field label="Year of Graduation"><Input type="text" value={yearOfGraduation} onChange={(e) => setYearOfGraduation(e.target.value)} /></Field>
                <Field label="Other Qualifications (optional)" optional><Input type="text" value={otherQualifications} onChange={(e) => setOtherQualifications(e.target.value)} /></Field>
              </div>
            </div>

            {/* 4 ── PROFESSIONAL CERTIFICATIONS */}
            <div className="appform__section">
              <SectionHeader title="Professional Certifications" />
              <p className="appform__section-sublabel">Optional but recommended</p>
              <div className="appform__row">
                <Field label="Certification Name"><Input type="text" value={certificationName} onChange={(e) => setCertificationName(e.target.value)} /></Field>
                <Field label="Issuing Body"><Input type="text" value={issuingBody} onChange={(e) => setIssuingBody(e.target.value)} /></Field>
              </div>
              <div className="appform__row appform__row--single">
                <Field label="Year Obtained"><Input type="text" value={yearObtained} onChange={(e) => setYearObtained(e.target.value)} /></Field>
              </div>
            </div>

            {/* 5 ── WORK EXPERIENCE */}
            <div className="appform__section">
              <SectionHeader title="Work Experience" />
              <p className="appform__section-sublabel">Your personal data collected is solely for recruitment purposes.</p>
              <div className="appform__row">
                <Field label="Current / Most Recent Employer"><Input type="text" value={currentEmployer} onChange={(e) => setCurrentEmployer(e.target.value)} /></Field>
                <Field label="Job Title"><Input type="text" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} /></Field>
              </div>
              <div className="appform__row appform__row--single">
                <div className="appform__group appform__group--full">
                  <label className="appform__label">Key Responsibilities &amp; Achievements</label>
                  <textarea className="appform__textarea" rows={3} value={keyResponsibilities} onChange={(e) => setKeyResponsibilities(e.target.value)} />
                </div>
              </div>
            </div>

            {/* 6 ── SKILLS & COMPETENCIES */}
            <div className="appform__section">
              <SectionHeader title="Skills & Competencies" />
              <div className="appform__row">
                <Field label="Technical Skills"><Input type="text" value={technicalSkills} onChange={(e) => setTechnicalSkills(e.target.value)} /></Field>
                <Field label="Soft Skills"><Input type="text" value={softSkills} onChange={(e) => setSoftSkills(e.target.value)} /></Field>
              </div>
              <div className="appform__row appform__row--single">
                <Field label="Computer / IT Proficiency"><Input type="text" value={itProficiency} onChange={(e) => setItProficiency(e.target.value)} /></Field>
              </div>
            </div>

            {/* 7 ── NYSC INFORMATION */}
            <div className="appform__section">
              <SectionHeader title="NYSC Information" />
              <CheckboxGroup label="NYSC Status" name="nysc_status"
                options={["Completed", "Currently Serving", "Exempted"]}
                selected={nyscStatus} onChange={setNyscStatus} />
              <div className="appform__row appform__row--single" style={{ marginTop: "16px" }}>
                <Field label="NYSC Certificate Number (if completed)"><Input type="text" value={nyscCertificateNumber} onChange={(e) => setNyscCertificateNumber(e.target.value)} /></Field>
              </div>
            </div>

            {/* 8 ── DOCUMENT UPLOADS */}
            <div className="appform__section">
              <SectionHeader title="Document Uploads" />
              <UploadBox label="Curriculum Vitae" hint="Import document here (PDF/DOC) file size 2mb" mandatory
                file={cvFile} onUpload={(e) => handleFileUpload(e, setCvFile)} inputId="cv-upload" accept=".pdf,.doc,.docx" />
              <UploadBox label="Cover Letter (Optional)" hint="Import document here (PDF/DOC) file size 2mb"
                file={coverLetterFile} onUpload={(e) => handleFileUpload(e, setCoverLetterFile)} inputId="cover-letter-upload" accept=".pdf,.doc,.docx" />
              <UploadBox label="Relevant Certifications 1" hint="Import Certificates here (PDF) file size 2mb"
                file={certificatesFile1} onUpload={(e) => handleFileUpload(e, setCertificatesFile1)} inputId="cert-upload-1" accept=".pdf" />
              <UploadBox label="Relevant Certifications 2" hint="Import Certificates here (PDF) file size 2mb"
                file={certificatesFile2} onUpload={(e) => handleFileUpload(e, setCertificatesFile2)} inputId="cert-upload-2" accept=".pdf" />
              <UploadBox label="Relevant Certifications 3" hint="Import Certificates here (PDF) file size 2mb"
                file={certificatesFile3} onUpload={(e) => handleFileUpload(e, setCertificatesFile3)} inputId="cert-upload-3" accept=".pdf" />
              <UploadBox label="Relevant Certifications 4" hint="Import Certificates here (PDF) file size 2mb"
                file={certificatesFile4} onUpload={(e) => handleFileUpload(e, setCertificatesFile4)} inputId="cert-upload-4" accept=".pdf" />
              <UploadBox label="Relevant Certifications 5" hint="Import Certificates here (PDF) file size 2mb"
                file={certificatesFile5} onUpload={(e) => handleFileUpload(e, setCertificatesFile5)} inputId="cert-upload-5" accept=".pdf" />
            </div>

            {/* 9 ── ADDITIONAL INFORMATION */}
            <div className="appform__section">
              <SectionHeader title="Additional Information" />
              <div className="appform__group appform__group--full" style={{ marginBottom: "20px" }}>
                <label className="appform__label">Why are you suitable for this role?</label>
                <textarea className="appform__textarea" rows={4} maxLength={maxChars}
                  value={whySuitable} onChange={(e) => setWhySuitable(e.target.value)} />
                <div className="appform__charcount">{whySuitable.length}/{maxChars}</div>
              </div>
              <CheckboxGroup label="Availability to Resume" name="availability"
                options={["Immediately", "2 weeks", "1 month", "Others"]}
                selected={availability} onChange={setAvailability} />
            </div>

            {submitError && <p className="appform__error">{submitError}</p>}
            {submitSuccess && <p className="appform__success">Application submitted successfully! Closing...</p>}
            <div className="appform__submit-wrap">
              <button type="submit" className="appform__submit-btn" disabled={submitting}>
                {submitting ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

/* ── Careers Page ── */
const CareersPage = () => {
  const cached = readCache();

  const [status, setStatus]     = useState(cached ? "ready" : "splash-loading");
  const [pageData, setPageData] = useState(cached || null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const doFetch = useCallback(async (background = false) => {
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const { data } = await axios.get(`${BASE_URL}/api/careers`, { signal: controller.signal });
      clearTimeout(timeoutId);

      const parsed = parseCareersData(data?.data);
      writeCache(parsed);
      setPageData(parsed);

      if (!background) setStatus("splash-done");
    } catch (err) {
      clearTimeout(timeoutId);
      if (background) {
        console.warn("Background refresh failed for /api/careers:", err.message);
      } else {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    doFetch(!!cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "error") {
    return <ErrorScreen onRetry={() => { setStatus("splash-loading"); doFetch(false); }} />;
  }
  if (status === "splash-loading") return <SplashScreen blocking />;
  // Defense in depth: don't fall through to the main render with no
  // usable data, whatever the reason — show the splash instead of
  // crashing on data.sections.map.
  if (!pageData) return <SplashScreen blocking />;

  const data = pageData;

  return (
    <>
      {status === "splash-done" && (
        <SplashScreen autoExit onDone={() => setStatus("ready")} />
      )}

      <div className="img_carrierscar">
        {data.bannerImage && <img src={data.bannerImage} alt="img" />}
      </div>

     <div className="careers-container" style={data.backgroundImage ? { backgroundImage: `url(${data.backgroundImage})` } : undefined}>
        <div className="careers-content">
          <div className="title-section">
            <h1 className="careers-title">{data.title}</h1>
            <div className="orange-underline"></div>
          </div>

          {(data.sections || []).map((sec, i) => (
            <React.Fragment key={i}>
              <p className="intro-text mkbold">{sec.heading}</p>
              {sec.paragraphs.map((p, j) => (
                <p className="intro-text" key={j}>{p}</p>
              ))}
            </React.Fragment>
          ))}

          <p className="intro-text mkbold">{data.whyWorkHeading}</p>
          <ul className="catext">
            {(data.whyWorkItems || []).map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>

          <button className="btn btn--orange" onClick={() => setIsFormOpen(true)}>{data.applyButtonLabel}</button>
        </div>

        {isFormOpen && <ApplicationForm onClose={() => setIsFormOpen(false)} />}
      </div>

      <Footer />
    </>
  );
};

export default CareersPage;